import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import type { Row } from "@tanstack/react-table";
import { DownloadIcon, Info, RefreshCcw } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { JsonView, allExpanded, defaultStyles } from "react-json-view-lite";

import { downloadExportFile } from "@/apis/exports/download";
import { retryExport } from "@/apis/exports/retry";
import { client } from "@/apis/gql/apolloClient";
import { AsyncButton, Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DATEV_EXPORTS_QUERY } from "@/queries/exports/get-all.legacy";
import { toast } from "sonner";
import { DatevExportType } from "./exportType";

interface DataTableRowActionsProps<TData> {
  row: Row<TData>;
}

const exportTypeMap = {
  [DatevExportType.BUCHUNGSDATENSERVICE_BOOKINGS]:
    "buchungsdatenservice-bookings",
  [DatevExportType.BUCHUNGSDATENSERVICE_SUPPLIERS]: "suppliers",
};

export function DataTableRowActions({ row }: DataTableRowActionsProps<any>) {
  const { t } = useTranslation();
  const [isDetailsOpened, setIsDetailsOpened] = React.useState(false);
  // FIXME: This is a workaround to prevent the dropdown from closing when the dialog is opened https://github.com/shadcn-ui/ui/issues/1912#issuecomment-2197220521
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  const handleDownload = async () => {
    try {
      await downloadExportFile(
        row.original.datevExportId,
        exportTypeMap[row.original.exportType as DatevExportType],
      );
    } catch (error) {
      toast.error(t("generic_error"), { richColors: true });
    }
  };

  const handleRetryExport = async () => {
    const toastId = toast.loading(t("generic_loading"));

    try {
      await retryExport(
        row.original.datevExportId,
        exportTypeMap[row.original.exportType as DatevExportType],
      );
      await client.refetchQueries({
        include: [DATEV_EXPORTS_QUERY],
      });
      toast.dismiss(toastId);
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
    }
  };

  return (
    <div className="flex flex-row items-center justify-end gap-4">
      <AsyncButton variant="outline" className="gap-2" onClick={handleDownload}>
        <DownloadIcon size={16} />
        {t("exports.table.download_file")}
      </AsyncButton>
      <DropdownMenu onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
          >
            <DotsHorizontalIcon className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={(e) => {
              setIsDetailsOpened(true);
            }}
            className="gap-2"
          >
            <Info size={14} />
            {t("exports.details")}
          </DropdownMenuItem>
          {row.original.state === "failed" && (
            <DropdownMenuItem className="gap-2" onSelect={handleRetryExport}>
              <RefreshCcw size={14} />
              {t("exports.retry")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog
        open={isDetailsOpened && !isDropdownOpen}
        onOpenChange={setIsDetailsOpened}
      >
        <DialogContent onCloseAutoFocus={(e) => e.preventDefault()}>
          <div className="flex flex-col gap-2">
            <JsonView
              data={row.original.results}
              shouldExpandNode={allExpanded}
              style={defaultStyles}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
